// Resource names are stored "Last, First" to match the billing/VMS Worker
// column and the QuickBooks service name. These helpers are the boundary
// between that storage format and human-facing prose (the subcontractor
// agreement's Project Personnel line, the e-sign signer name, email
// greetings), where "Last, First" reads wrong. The comma split mirrors
// billing-transform's splitWorker — first comma separates last from first.

export function splitLastFirst(name: string): { last: string; first: string } {
  const idx = name.indexOf(',');
  if (idx === -1) return { last: name.trim(), first: '' };
  return { last: name.slice(0, idx).trim(), first: name.slice(idx + 1).trim() };
}

/**
 * "Blow, Joe" -> "Joe Blow" for display in prose. A name with no comma (or an
 * empty side) is returned trimmed as-is rather than mangled — storage is
 * comma-validated, so this only guards legacy/edge data.
 */
export function naturalName(name: string): string {
  const { last, first } = splitLastFirst(name);
  if (first === '' || last === '') return name.trim();
  return `${first} ${last}`;
}

/**
 * Validates the "Last, First" entry format: exactly the presence of a comma
 * with non-empty content on both sides. Used by the onboarding name field on
 * both the FE form and the shared backend schema so an admin can't type
 * "First Last" by mistake.
 */
export function isLastCommaFirst(name: string): boolean {
  const { last, first } = splitLastFirst(name);
  return name.includes(',') && last !== '' && first !== '';
}

/**
 * The `FirstnameLastname` local part for the uIT O365 mailbox (T-114/T-167),
 * derived from the stored "Last, First" name — no dot, each name capitalized
 * ("Akula, Kalyan" → "KalyanAkula"), per Eric 2026-07-03.
 *
 * Each segment is split on non-alphanumerics and every word is Title-cased, so
 * internal word boundaries survive the join: "O'Brien" → "OBrien",
 * "Smith-Jones" → "SmithJones", a middle name "Maria Luisa" → "MariaLuisa".
 * Title-casing rather than preserving the source's capitalization is deliberate:
 * CSV-imported rows carry ALL-CAPS names, which would otherwise mint
 * "KALYANAKULA@". Non-ASCII is stripped, as Entra's mailNickname wants ASCII.
 *
 * Returns '' when either segment is empty (no comma / legacy data) — the caller
 * treats that as "can't derive a mailbox" rather than minting a malformed
 * address. This is only the BASE; collision handling (the numeric suffix) lives
 * in the Graph service.
 */
export function uitMailboxLocalPart(name: string): string {
  const { last, first } = splitLastFirst(name);
  const seg = (s: string): string =>
    s
      .split(/[^a-zA-Z0-9]+/)
      .filter((word) => word !== '')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('');
  const f = seg(first);
  const l = seg(last);
  if (f === '' || l === '') return '';
  return `${f}${l}`;
}
